import { useState, useRef, useEffect } from "react";
import { Upload, FileSpreadsheet, Check, X, AlertCircle, ChevronRight, ChevronLeft, Loader2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SOURCES = [
  "Google Ads", "Facebook", "Facebook Ads", "Website", "Walk-in",
  "Referral", "Email", "WhatsApp", "Instagram", "LinkedIn", "Manual", "Other"
];

const LeadImportDialog = ({ open, onOpenChange, onImportComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [excelColumns, setExcelColumns] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [columnMapping, setColumnMapping] = useState({});
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [previewResult, setPreviewResult] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // Source selection state
  const [selectedSource, setSelectedSource] = useState("");
  const [overrideSource, setOverrideSource] = useState(true);
  const [showNewSource, setShowNewSource] = useState(false);
  const [newSourceName, setNewSourceName] = useState("");
  const [sourceError, setSourceError] = useState("");
  const [availableSources, setAvailableSources] = useState(DEFAULT_SOURCES);

  const [leadFields, setLeadFields] = useState([
    { key: "name", label: "Name *" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "company", label: "Company" },
    { key: "city", label: "City" },
    { key: "stage", label: "Stage" },
    { key: "source", label: "Source" },
    { key: "deal_value", label: "Deal Value" },
  ]);

  const fileInputRef = useRef(null);
  const TOTAL_STEPS = 5;

  // Fetch dynamic lead fields and sources when dialog opens
  useEffect(() => {
    if (open) {
      (async () => {
        try {
          const [sourcesRes, fieldsRes] = await Promise.all([
            fetch(`${BACKEND_URL}/api/lead-sources/names`, { credentials: "include" }),
            fetch(`${BACKEND_URL}/api/lead-fields`, { credentials: "include" }),
          ]);
          if (sourcesRes.ok) {
            const data = await sourcesRes.json();
            if (data.names && data.names.length > 0) {
              setAvailableSources(data.names);
            }
          }
          if (fieldsRes.ok) {
            const fields = await fieldsRes.json();
            const dynamicFields = fields
              .filter(f => f.is_active !== false && f.field_key !== "created_at")
              .map(f => ({
                key: f.field_key,
                label: f.is_required ? (f.field_label + " *") : f.field_label,
              }));
            // Add system fields not in form builder
            const keys = new Set(dynamicFields.map(f => f.key));
            if (!keys.has("assigned_to")) dynamicFields.push({ key: "assigned_to", label: "Assigned To" });
            if (dynamicFields.length > 0) setLeadFields(dynamicFields);
          }
        } catch (err) { /* fallback to defaults */ }
      })();
    }
  }, [open]);

  const resetState = () => {
    setStep(1); setFileData(null); setFileName(""); setExcelColumns([]);
    setTotalRows(0); setColumnMapping({}); setPreviewResult(null);
    setImportResult(null); setSelectedSource(""); setOverrideSource(true);
    setShowNewSource(false); setNewSourceName(""); setSourceError("");
  };

  const handleClose = () => { resetState(); onOpenChange(false); };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Please select an Excel file (.xlsx or .xls)"); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB"); return;
    }
    setFileName(file.name);
    setLoading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Data = reader.result;
      setFileData(base64Data);
      try {
        const response = await fetch(`${BACKEND_URL}/api/leads/import/parse`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ file_data: base64Data }),
        });
        if (response.ok) {
          const data = await response.json();
          setExcelColumns(data.columns);
          setTotalRows(data.total_rows);
          const autoMapping = {};
          leadFields.forEach((field) => {
            const matched = data.columns.find(
              (col) => col.toLowerCase().includes(field.key.toLowerCase())
            );
            if (matched) autoMapping[field.key] = matched;
          });
          setColumnMapping(autoMapping);
          setStep(2); // Go to source selection step
          toast.success(`File parsed: ${data.total_rows} rows found`);
        } else {
          const error = await response.json();
          toast.error(error.detail || "Failed to parse file");
        }
      } catch (err) { toast.error("Failed to parse file"); }
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSourceNext = () => {
    if (!selectedSource) {
      setSourceError("Lead Source is required before importing.");
      return;
    }
    setSourceError("");
    setStep(3);
  };

  const handleAddNewSource = () => {
    const trimmed = newSourceName.trim();
    if (!trimmed) return;
    setSelectedSource(trimmed);
    setShowNewSource(false);
    setNewSourceName("");
    toast.success(`Source "${trimmed}" selected`);
  };

  // Find the first required field key (the one with " *" in label)
  const requiredFieldKey = leadFields.find(f => f.label.includes("*"))?.key || leadFields[0]?.key || "name";

  const handlePreview = async () => {
    if (!columnMapping[requiredFieldKey]) {
      const reqLabel = leadFields.find(f => f.key === requiredFieldKey)?.label?.replace(" *", "") || "Name";
      toast.error(`Please map the ${reqLabel} column (required)`); return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/import/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ file_data: fileData, column_mapping: columnMapping }),
      });
      if (response.ok) {
        const data = await response.json();
        setPreviewResult(data);
        setStep(4);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to preview");
      }
    } catch (err) { toast.error("Failed to preview"); }
    setLoading(false);
  };

  const handleImport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/import/execute`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          file_data: fileData,
          column_mapping: columnMapping,
          skip_duplicates: skipDuplicates,
          lead_source: selectedSource,
          override_source: overrideSource
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setImportResult(data);
        setStep(5);
        toast.success(`Imported ${data.imported_count} leads`);
        if (onImportComplete) onImportComplete();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to import");
      }
    } catch (err) { toast.error("Failed to import"); }
    setLoading(false);
  };

  // Check if Excel has source column mapped
  const hasExcelSourceColumn = !!columnMapping.source;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto w-[95vw] max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            Import Leads from Excel
          </DialogTitle>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-1.5 py-2">
          {[
            { n: 1, label: "Upload" },
            { n: 2, label: "Source" },
            { n: 3, label: "Map" },
            { n: 4, label: "Preview" },
            { n: 5, label: "Done" }
          ].map((s) => (
            <div key={s.n} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${step >= s.n ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                  {step > s.n ? <Check className="w-3 h-3" /> : s.n}
                </div>
                <span className="text-[9px] text-slate-400 mt-0.5">{s.label}</span>
              </div>
              {s.n < TOTAL_STEPS && <div className={`w-5 h-0.5 mt-[-10px] ${step > s.n ? "bg-indigo-600" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className={`border-2 border-dashed rounded-xl p-6 text-center ${loading ? "border-indigo-300 bg-indigo-50" : "border-slate-200 hover:border-indigo-400"}`}>
              {loading ? (
                <div className="flex flex-col items-center">
                  <div className="relative w-14 h-14 mb-3">
                    <div className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
                    <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-indigo-600 animate-spin" />
                    <div className="absolute inset-2 rounded-full border-[2px] border-transparent border-b-violet-500 animate-spin" style={{ animationDirection: "reverse", animationDuration: "0.8s" }} />
                  </div>
                  <p className="text-sm font-medium text-slate-700">Processing your file...</p>
                  <div className="w-48 mt-3 progress-bar-indeterminate" />
                </div>
              ) : (
                <div>
                  <Upload className="w-10 h-10 text-slate-400 mx-auto" />
                  <p className="mt-3 text-slate-600">Drop Excel file here or</p>
                  <Button onClick={() => fileInputRef.current?.click()} className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">
                    Browse Files
                  </Button>
                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" data-testid="file-input" />
                  <p className="mt-3 text-xs text-slate-400">Formats: .xlsx, .xls (Max 10MB)</p>
                </div>
              )}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
              <p className="font-medium text-amber-700 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Tips</p>
              <ul className="mt-1 text-amber-600 text-xs space-y-0.5">
                <li>First row should be headers</li>
                <li>Name column is required</li>
                <li>Email/Phone used for duplicate detection</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Select Lead Source */}
        {step === 2 && (
          <div className="space-y-4 py-4" data-testid="source-selection-step">
            <div className="text-center mb-2">
              <p className="text-sm text-slate-600">Select the source for <strong>{totalRows}</strong> leads from <strong>{fileName}</strong></p>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 space-y-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Lead Source *</Label>
                {!showNewSource ? (
                  <div className="mt-2 space-y-2">
                    <Select value={selectedSource} onValueChange={(v) => { setSelectedSource(v); setSourceError(""); }}>
                      <SelectTrigger className="bg-white rounded-xl h-11" data-testid="source-select">
                        <SelectValue placeholder="Select lead source..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => setShowNewSource(true)}
                      className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      data-testid="add-new-source-btn"
                    >
                      <Plus className="w-4 h-4" /> Add New Source
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-2">
                    <Input
                      value={newSourceName}
                      onChange={e => setNewSourceName(e.target.value)}
                      placeholder="Enter new source name..."
                      className="flex-1 bg-white rounded-xl h-11"
                      data-testid="new-source-input"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === "Enter") handleAddNewSource(); }}
                    />
                    <Button size="sm" onClick={handleAddNewSource} disabled={!newSourceName.trim()} className="rounded-xl h-11" data-testid="save-new-source-btn">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setShowNewSource(false); setNewSourceName(""); }} className="rounded-xl h-11">Cancel</Button>
                  </div>
                )}
                {sourceError && (
                  <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {sourceError}
                  </p>
                )}
              </div>

              {selectedSource && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    Selected: <strong>{selectedSource}</strong> — will be assigned to all {totalRows} leads
                  </p>
                </div>
              )}
            </div>

            {/* Override toggle - only show if Excel has source column */}
            {hasExcelSourceColumn && selectedSource && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <Checkbox
                  checked={overrideSource}
                  onCheckedChange={setOverrideSource}
                  className="mt-0.5"
                  data-testid="override-source-checkbox"
                />
                <div>
                  <p className="text-sm font-medium text-blue-800">Override Excel Source column</p>
                  <p className="text-xs text-blue-600 mt-0.5">
                    {overrideSource
                      ? `All leads will use "${selectedSource}" regardless of Excel data`
                      : "Leads with source in Excel will keep their original source"}
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button onClick={() => setStep(1)} variant="outline" className="rounded-full">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={handleSourceNext} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full" data-testid="source-next-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Map Columns */}
        {step === 3 && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">Map Excel columns to lead fields ({totalRows} rows)</p>
            <div className="bg-slate-50 rounded-xl p-3 space-y-2">
              {leadFields.map((field) => (
                <div key={field.key} className="flex items-center gap-3">
                  <Label className="w-24 text-sm shrink-0">{field.label}</Label>
                  <Select value={columnMapping[field.key] || ""} onValueChange={(v) => setColumnMapping({ ...columnMapping, [field.key]: v === "_none" ? "" : v })}>
                    <SelectTrigger className="flex-1 bg-white rounded-lg text-sm">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">-- Skip --</SelectItem>
                      {excelColumns.map((col) => <SelectItem key={col} value={col}>{col}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex justify-between">
              <Button onClick={() => setStep(2)} variant="outline" className="rounded-full">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={handlePreview} disabled={loading || !columnMapping[requiredFieldKey]} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">
                {loading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Preview <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Preview */}
        {step === 4 && previewResult && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-slate-100 rounded-lg p-2 text-center">
                <p className="text-lg font-bold">{previewResult.total_rows}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
              <div className="bg-green-100 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-green-700">{previewResult.valid_count}</p>
                <p className="text-xs text-green-600">Valid</p>
              </div>
              <div className="bg-amber-100 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-amber-700">{previewResult.duplicate_count}</p>
                <p className="text-xs text-amber-600">Duplicates</p>
              </div>
              <div className="bg-red-100 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-red-700">{previewResult.invalid_count}</p>
                <p className="text-xs text-red-600">Invalid</p>
              </div>
            </div>

            {/* Source badge */}
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg">
              <span className="text-xs text-indigo-600 font-medium">Source:</span>
              <Badge className="bg-indigo-100 text-indigo-700 text-xs">{selectedSource}</Badge>
              {overrideSource && hasExcelSourceColumn && (
                <span className="text-[10px] text-indigo-400">(overriding Excel)</span>
              )}
            </div>

            <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium">Skip Duplicates</p>
                <p className="text-xs text-slate-500">Skip leads with existing emails/phones</p>
              </div>
              <button onClick={() => setSkipDuplicates(!skipDuplicates)} className={`w-10 h-5 rounded-full transition-colors ${skipDuplicates ? "bg-indigo-600" : "bg-slate-300"}`}>
                <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${skipDuplicates ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>

            <div className="bg-white border rounded-lg max-h-40 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left">Row</th>
                    <th className="px-2 py-1 text-left">Name</th>
                    <th className="px-2 py-1 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {previewResult.preview.slice(0, 10).map((lead, idx) => (
                    <tr key={idx} className={lead.is_duplicate ? "bg-amber-50" : lead.is_valid ? "" : "bg-red-50"}>
                      <td className="px-2 py-1">{lead.row_number}</td>
                      <td className="px-2 py-1">{lead.name || "-"}</td>
                      <td className="px-2 py-1">
                        {lead.is_duplicate ? <Badge className="bg-amber-100 text-amber-700 text-xs">Dup</Badge>
                          : lead.is_valid ? <Badge className="bg-green-100 text-green-700 text-xs">OK</Badge>
                          : <Badge className="bg-red-100 text-red-700 text-xs">Err</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between">
              <Button onClick={() => setStep(3)} variant="outline" className="rounded-full">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={handleImport} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white rounded-full disabled:opacity-60" data-testid="import-execute-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                {loading ? "Importing..." : `Import ${skipDuplicates ? previewResult.valid_count - previewResult.duplicate_count : previewResult.valid_count}`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Complete */}
        {step === 5 && importResult && (
          <div className="space-y-4 py-4 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="text-lg font-bold">Import Complete!</h3>
            <p className="text-slate-600">Imported {importResult.imported_count} leads from <strong>{selectedSource}</strong></p>

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-green-100 rounded-lg p-2">
                <p className="text-lg font-bold text-green-700">{importResult.imported_count}</p>
                <p className="text-xs text-green-600">Imported</p>
              </div>
              <div className="bg-amber-100 rounded-lg p-2">
                <p className="text-lg font-bold text-amber-700">{importResult.skipped_count}</p>
                <p className="text-xs text-amber-600">Skipped</p>
              </div>
              <div className="bg-red-100 rounded-lg p-2">
                <p className="text-lg font-bold text-red-700">{importResult.error_count}</p>
                <p className="text-xs text-red-600">Errors</p>
              </div>
            </div>

            <Button onClick={handleClose} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadImportDialog;
